import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import type { ReactElement } from 'react';
import { cn } from '../util/classes.ts';

export function TooltipProvider({ delay = 400, ...props }: TooltipPrimitive.Provider.Props): ReactElement {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
}

export function Tooltip({ ...props }: TooltipPrimitive.Root.Props): ReactElement {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

export function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props): ReactElement {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

export type TooltipContentProps = TooltipPrimitive.Popup.Props &
    Pick<TooltipPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'>;

export function TooltipContent({
    align = 'center',
    alignOffset = 0,
    children,
    className,
    side = 'top',
    sideOffset = 4,
    ...props
}: TooltipContentProps): ReactElement {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Positioner
                className="isolate z-(--z-tooltip)"
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
            >
                <TooltipPrimitive.Popup
                    className={cn(
                        'z-(--z-tooltip) w-fit max-w-xs origin-(--transform-origin)',
                        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5',
                        'bg-content text-ui-base text-container',
                        'has-data-[slot=kbd]:pr-1.5',
                        'data-[side=bottom]:slide-in-from-top-2',
                        'data-[side=inline-end]:slide-in-from-left-2',
                        'data-[side=inline-start]:slide-in-from-right-2',
                        'data-[side=left]:slide-in-from-right-2',
                        'data-[side=right]:slide-in-from-left-2',
                        'data-[side=top]:slide-in-from-bottom-2',
                        '**:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate',
                        '**:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm',
                        'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
                        'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
                        'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',

                        className,
                    )}
                    {...props}
                >
                    {children}
                    <TooltipPrimitive.Arrow
                        className={cn(
                            'z-(--z-tooltip) size-2.5',
                            'translate-y-[calc(-50%-2px)] rotate-45',
                            'rounded-xs bg-foreground fill-foreground',
                            'data-[side=bottom]:top-1',
                            'data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2',
                            'data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2',
                            'data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2',
                            'data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2',
                            'data-[side=top]:-bottom-2.5',
                        )}
                    />
                </TooltipPrimitive.Popup>
            </TooltipPrimitive.Positioner>
        </TooltipPrimitive.Portal>
    );
}
